import React, { useState } from 'react';

import cognitoAuthService from '../../services/cognitoAuthService';
import { FILLS, SIZING, TYPE } from '../../styles/tokens';
import {
  PASSWORD_EDGE_SPACE_MESSAGE,
  PASSWORD_TOO_SHORT_MESSAGE,
  hasEdgeWhitespace,
  isPasswordLongEnough,
} from '../../utils/authFormValidation';
import { SheetFooter } from '../common/Sheet';
import NewPasswordHint from './NewPasswordHint';

export const FORGOT_PASSWORD_HEADING = 'Reset your password';
export const FORGOT_PASSWORD_BACK_LABEL = 'Back to log in';

const NEW_PASSWORD_HINT_ID = 'forgot-password-hint';
const ERROR_MESSAGE_ID = 'forgot-password-error-message';
const REQUEST_FORM_ID = 'forgot-password-request-form';
const CONFIRM_FORM_ID = 'forgot-password-confirm-form';

type ForgotPasswordStep = 'request' | 'confirm';

export interface ForgotPasswordFormProps {
  /** The reset finished; the host returns the guest to Log in with this e-mail filled. */
  onReset: (email: string) => void;
  /** The guest left the reset without finishing it. */
  onBack: () => void;
  /** Sign-in pop-up only: the primary button renders in the sheet's pinned footer. */
  pinSubmitToSheetFooter?: boolean;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onReset,
  onBack,
  pinSubmitToSheetFooter = false,
}) => {
  const [step, setStep] = useState<ForgotPasswordStep>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await cognitoAuthService.forgotPassword(email.trim());
      setStep('confirm');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (hasEdgeWhitespace(newPassword)) {
      setError(PASSWORD_EDGE_SPACE_MESSAGE);
      return;
    }
    if (!isPasswordLongEnough(newPassword)) {
      setError(PASSWORD_TOO_SHORT_MESSAGE);
      return;
    }

    setIsLoading(true);

    try {
      const trimmedEmail = email.trim();
      await cognitoAuthService.confirmForgotPassword({
        email: trimmedEmail,
        code: code.trim(),
        new_password: newPassword,
      });
      onReset(trimmedEmail);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
      setIsLoading(false);
    }
  };

  const formId = step === 'request' ? REQUEST_FORM_ID : CONFIRM_FORM_ID;
  const submitButton = (
    <button
      type="submit"
      form={formId}
      className="btn-gold"
      disabled={isLoading}
      style={pinSubmitToSheetFooter ? undefined : { marginTop: '24px' }}
    >
      {isLoading ? (
        <span className="spinner" style={{ width: '24px', height: '24px', margin: '0 auto' }} />
      ) : step === 'request' ? (
        'Send Reset Code'
      ) : (
        'Reset Password'
      )}
    </button>
  );
  const backButton = (
    <button
      type="button"
      onClick={onBack}
      disabled={isLoading}
      style={{
        display: 'block',
        margin: '8px auto 0',
        minHeight: SIZING.touchTarget,
        padding: '8px 16px',
        color: FILLS.gold,
        background: 'none',
        border: 'none',
        cursor: isLoading ? 'default' : 'pointer',
        ...TYPE.secondary,
        fontWeight: 500,
      }}
    >
      {FORGOT_PASSWORD_BACK_LABEL}
    </button>
  );

  return (
    <div>
      <h3 style={{ ...TYPE.itemName, color: FILLS.textPrimary, marginTop: 0, marginBottom: '8px' }}>
        {FORGOT_PASSWORD_HEADING}
      </h3>
      <p className="form-subtext" style={{ textAlign: 'left' }}>
        {step === 'request'
          ? 'Enter your email and we will send you a reset code'
          : `We sent a reset code to ${email}`}
      </p>

      {error && (
        <div
          className="alert alert-error"
          id={ERROR_MESSAGE_ID}
          role="alert"
          style={{ marginBottom: '16px' }}
        >
          {error}
        </div>
      )}

      {step === 'request' ? (
        <form id={REQUEST_FORM_ID} onSubmit={handleRequestSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="forgot-password-email">
              Email Address
            </label>
            <input
              id="forgot-password-email"
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              autoFocus
              aria-describedby={error ? ERROR_MESSAGE_ID : undefined}
            />
          </div>
        </form>
      ) : (
        <form id={CONFIRM_FORM_ID} onSubmit={handleConfirmSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="forgot-password-code">
              Reset Code
            </label>
            <input
              id="forgot-password-code"
              className="form-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              aria-describedby={error ? ERROR_MESSAGE_ID : undefined}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="forgot-password-new-password">
              New Password
            </label>
            <input
              id="forgot-password-new-password"
              className="form-input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              aria-describedby={error ? ERROR_MESSAGE_ID : NEW_PASSWORD_HINT_ID}
            />
            <NewPasswordHint id={NEW_PASSWORD_HINT_ID} password={newPassword} />
          </div>
        </form>
      )}

      {pinSubmitToSheetFooter ? (
        <SheetFooter>
          {submitButton}
          {backButton}
        </SheetFooter>
      ) : (
        <>
          {submitButton}
          {backButton}
        </>
      )}
    </div>
  );
};

export default ForgotPasswordForm;
